import { RedStarIcon } from '@/assets/icons/info-icons/redStar';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import styled from 'styled-components';
import { FieldRowProps } from '../types';
import { FieldActions } from './FieldActions';
import { FieldValue } from './FieldValue';

export const FieldRow = ({
  field,
  index,
  mode,
  onEditField,
  onAcceptAiAnswer,
  onClearField,
  onOverrideAnswer,
  onRelinkField,
  showAcceptButton = true,
  showClearButton = true,
  showEditButton = true,
  customFieldRenderer,
  isDisabled = false
}: FieldRowProps) => {
  const { isMobile } = useResponsive();

  if (mode === 'view') {
    return (
      <ViewModeSection id={field.name}>
        <ViewModeSectionName>{field.name}</ViewModeSectionName>
        <ViewModeFieldValueWrapper>
          <FieldValue field={field} index={index} mode={mode} customRenderer={customFieldRenderer} />
        </ViewModeFieldValueWrapper>
      </ViewModeSection>
    );
  }

  return (
    <RowWrapper isMobile={isMobile}>
      <FieldNameWrapper>
        {field.linked ? <img src="/static/icons/lock-icon.png" /> : field.required ? <RedStarIcon width={10} height={10} /> : <NoRequired />}
        <FieldName id="fieldNameText" onClick={() => onEditField?.(field)}>
          {field.name}
        </FieldName>
      </FieldNameWrapper>
      <FieldValue field={field} index={index} mode={mode} customRenderer={customFieldRenderer} />
      <FieldActions
        field={field}
        mode={mode}
        onEditField={onEditField}
        onAcceptAiAnswer={onAcceptAiAnswer}
        onClearField={onClearField}
        onOverrideAnswer={onOverrideAnswer}
        onRelinkField={onRelinkField}
        showAcceptButton={showAcceptButton}
        showClearButton={showClearButton}
        showEditButton={showEditButton}
        isDisabled={isDisabled}
      />
    </RowWrapper>
  );
};

const RowWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'center')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '20px' : '10px')};
  padding: 28px 12px;
  border-bottom: 1px solid ${themeVar('border.base')};

  & #actionDropdown,
  & #acceptAiAnswerButton,
  & #overrideAnswerButton,
  & #relinkFieldButton {
    opacity: ${({ isMobile }) => (isMobile ? '1' : '0')};
  }

  &:hover #actionDropdown,
  &:hover #acceptAiAnswerButton,
  &:hover #overrideAnswerButton,
  &:hover #relinkFieldButton {
    opacity: 1;
  }

  &:hover #fieldNameText {
    color: ${themeVar('font.action')};
  }

  &:hover {
    background-color: ${themeVar('layer.2')};
    transition: background-color 100ms ease;
  }
`;

const FieldNameWrapper = styled.div`
  min-width: 20%;
  max-width: 20%;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${themeVar('font.strong')};
  display: flex;
  gap: 10px;
  align-items: center;
`;

const FieldName = styled.div`
  cursor: pointer;
`;

const NoRequired = styled.div`
  width: 10px;
`;

const ViewModeSection = styled.div`
  display: flex;
  width: 100%;
`;

const ViewModeSectionName = styled.div`
  width: 25%;
  min-width: 320px;
  padding: 20px 30px 20px 50px;
  font-size: 23px;
  font-weight: 700;
  line-height: 28px;
  color: ${themeVar('font.strong')};
  border-right: 5px solid ${themeVar('menu.active')};
`;

const ViewModeFieldValueWrapper = styled.div`
  padding: 0 40px 0 20px;
  width: 75%;
`;
